<template>
  <div class="ci-summary-group">
    <div class="ci-summary-title ci-card-title ci-accent ci-accent--attention">
      <div class="ci-title-left">
        Open Conditions After Approval
        <span class="ci-summary-subtitle">Rolling {{ rollingDays }} days</span>
      </div>

      <div class="ci-title-right">
        <input
          class="ci-mini-search"
          v-model="searchText"
          type="text"
          placeholder="Search..."
        />

        <select class="ci-mini-select" v-model="sortBy">
      
          <option value="orgid">Org</option>
          <option value="underwriter">UW</option>
          <option value="loanOfficer">LO</option>
          <option value="division">Division</option>
        </select>
      </div>
    </div>

    <div class="ci-summary-cards ci-scroll">
      <div v-if="loading" class="ci-summary-loading">Loading...</div>
      <div v-else-if="error" class="ci-empty-inline ci-empty-inline--error">{{ error }}</div>

      <template v-else>
        <div v-if="groupedSummaryFiltered.length" class="ci-list">
          <button
            v-for="g in groupedSummaryFiltered"
            :key="g.groupKey"
            class="ci-summary-row ci-summary-row--click"
            type="button"
            @click="openGroupModal(g)"
          >
            <div class="ci-summary-namewrap">
              <div class="ci-summary-name" :title="g.groupTitle">
                {{ g.groupTitle }}
              </div>

              <div class="ci-summary-sub" :title="g.groupSub">
                {{ g.groupSub }}
              </div>
            </div>

<div class="ci-summary-right">
  <div class="ci-summary-top">
    <div class="ci-summary-metric ci-summary-metric--muted">
      <span class="ci-metric-label">Loans</span>
      <span class="ci-metric-value">{{ fmtInt(g.loanCount) }}</span>
    </div>

    <div class="ci-summary-metric">
      <span class="ci-metric-label">Open</span>
      <span class="ci-metric-value">{{ fmtInt(g.openConditionCount) }}</span>
    </div>

  
  </div>
    <div class="ci-summary-metric ci-metric-larger-font">
      <span class="ci-metric-label">Avg</span>
      <span class="ci-metric-value">{{ fmtDec(g.avgOpenPerLoan) }}</span>
    </div>
</div>


          </button>
        </div>

        <div v-else class="ci-empty-inline">
          No activity in this range
        </div>
      </template>
    </div>

    <div v-if="showModal" class="ci-modal" @click.self="closeModal">
      <div class="ci-modal__panel">
        <div class="ci-modal__header">
          <div class="ci-modal__head-left">
            <div class="ci-modal__title">{{ modalTitle }}</div>

            <div class="ci-modal__meta" v-if="modalMode === 'group'">
              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label">Loans</span>
                <span class="ci-pill-value">{{ fmtInt(selectedGroup?.loanCount) }}</span>
              </div>

              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label">Avg</span>
                <span class="ci-pill-value">{{ fmtDec(selectedGroup?.avgOpenPerLoan) }}</span>
              </div>

              <div class="ci-summary-metric">
                <span class="ci-pill-label">Open</span>
                <span class="ci-pill-value">{{ fmtInt(selectedGroup?.openConditionCount) }}</span>
              </div>
            </div>

            <div class="ci-modal__meta" v-else>
              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label">UW</span>
                <span class="ci-pill-value">{{ selectedUw }}</span>
              </div>

              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label">Sub</span>
                <span class="ci-pill-value">{{ selectedSub }}</span>
              </div>

              <div class="ci-summary-metric ci-summary-metric--muted">
                <span class="ci-pill-label">Approved</span>
                <span class="ci-pill-value">{{ selectedApproved }}</span>
              </div>

              <div class="ci-summary-metric">
                <span class="ci-pill-label">Open</span>
                <span class="ci-pill-value">{{ selectedOpen }}</span>
              </div>
            </div>
          </div>

          <div class="ci-modal__head-right">
            <button
              v-if="modalMode === 'loan'"
              class="ci-btn ci-btn--ghost"
              type="button"
              @click="backToGroup()"
            >
              Back
            </button>
            <button class="ci-modal__close" type="button" @click="closeModal">✕</button>
          </div>
        </div>

        <div class="ci-modal__body ci-scroll">
          <template v-if="modalMode === 'group'">
            <div v-if="groupLoans.length" class="ci-list">
              <button
                v-for="r in groupLoans"
                :key="String(r.loanId)"
                class="ci-summary-row ci-summary-row--click"
                type="button"
                @click="openLoanModal(r)"
              >
                <div class="ci-summary-namewrap">
                  <div class="ci-summary-name" :title="loanTitle(r)">
                    {{ loanTitle(r) }}
                  </div>
                  <div class="ci-summary-sub" :title="loanSub(r)">
                    {{ loanSub(r) }}
                  </div>
                </div>

                <div class="ci-summary-right">
                  <div class="ci-summary-metric ci-summary-metric--muted">
                    <span class="ci-pill-label">Sub. Count</span>
                    <span class="ci-pill-value">{{ fmtInt(r.submittalCounter) }}</span>
                  </div>

                  <div class="ci-summary-metric">
                    <span class="ci-pill-label">Open</span>
                    <span class="ci-pill-value">{{ fmtInt(r.openConditionCount) }}</span>
                  </div>
                </div>
              </button>
            </div>
            <div v-else class="ci-empty-inline">No loans found</div>
          </template>

          <template v-else>
            <div v-if="detailsLoading" class="ci-summary-loading">Loading details...</div>
            <div v-else-if="detailsError" class="ci-empty-inline ci-empty-inline--error">{{ detailsError }}</div>

            <template v-else>
              <div v-if="detailRows.length" class="ci-detail-list">
                <div v-for="d in detailRows" :key="detailKey(d)" class="ci-detail-card">
                  <div class="ci-detail-head">
                    <div class="ci-detail-title-row">
                      <div class="ci-detail-title" :title="d.title">{{ d.title }}</div>

                      <div class="ci-detail-badges">
                        <div v-if="d.latestStatusAfterCondApproval" class="ci-badge">
                          {{ d.latestStatusAfterCondApproval }}
                        </div>
                      </div>
                    </div>

                    <div class="ci-detail-chips">
                      <div class="ci-detail-chip">
                        <span class="ci-detail-chip-label">Created</span>
                        <span class="ci-detail-chip-value">{{ fmtDateTime(d.createdAt) }}</span>
                      </div>

                      <div class="ci-detail-chip">
                        <span class="ci-detail-chip-label">Updated</span>
                        <span class="ci-detail-chip-value">{{ fmtDateTime(d.updatedAt) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="ci-empty-inline">No open conditions found</div>
            </template>
          </template>
        </div>

        <div class="ci-modal__footer">
          <button class="ci-btn" type="button" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

export default {
  name: "OpenConditionsAfterCondApprovalSummaryCard",
  props: {
    authToken: { type: String, default: "" },
    rollingDays: { type: Number, default: 90 }
  },
  data() {
    return {
      loading: false,
      error: "",
      rows: [],

      sortBy: "orgid",
      searchText: "",

      showModal: false,
      modalMode: "group",
      selectedGroup: null,
      selectedLoan: null,

      detailsLoading: false,
      detailsError: "",
      detailRows: []
    }
  },
  computed: {
    groupedSummary() {
      const map = {}

      for (const r of this.rows || []) {
        const k = this.groupKeyForRow(r, this.sortBy)
        if (!k) continue

        if (!map[k]) {
          map[k] = {
            groupKey: k,
            groupTitle: this.groupTitleForRow(r, this.sortBy),
            groupSub: this.groupSubForRow(r, this.sortBy),
            openConditionCount: 0,
            loans: {}
          }
        }

        const loanKey = r.loanId || r.loanNumber
        if (loanKey) {
          if (!map[k].loans[loanKey]) map[k].loans[loanKey] = true
        }

        map[k].openConditionCount += Number(r.openConditionCount || 0)
      }

      const list = Object.values(map).map(x => {
        const loanCount = Object.keys(x.loans).length
        const open = Number(x.openConditionCount || 0)
        return {
          groupKey: x.groupKey,
          groupTitle: x.groupTitle,
          groupSub: x.groupSub,
          openConditionCount: open,
          loanCount,
          avgOpenPerLoan: loanCount ? open / loanCount : 0
        }
      })

      return this.sortGroupedList(list)
    },

    groupedSummaryFiltered() {
      const q = String(this.searchText || "").trim().toLowerCase()
      if (!q) return this.groupedSummary

      return this.groupedSummary.filter(g => {
        const hay = [g.groupKey, g.groupTitle, g.groupSub].filter(Boolean).join(" ").toLowerCase()
        return hay.includes(q)
      })
    },

    groupLoans() {
      if (!this.selectedGroup) return []
      const rows = (this.rows || []).filter(r => this.groupKeyForRow(r, this.sortBy) === this.selectedGroup.groupKey)
      return this.groupLoansByLoan(rows)
    },

    modalTitle() {
      if (this.modalMode === "group") return this.selectedGroup?.groupTitle || "Details"
      return this.selectedLoan ? this.loanTitle(this.selectedLoan) : "Loan"
    },

    selectedUw() {
      return this.selectedLoan?.underwriterDisplayName || "—"
    },
    selectedSub() {
      return this.fmtDate(this.selectedLoan?.logMSDateSubmittalDt)
    },
    selectedApproved() {
      return this.fmtDate(this.selectedLoan?.logMSDateCondlApprovalDt)
    },
    selectedOpen() {
      return this.fmtInt(this.selectedLoan?.openConditionCount)
    }
  },
  watch: {
    authToken: {
      immediate: true,
      handler() {
        if (!this.authToken) return
        this.fetchSummary()
      }
    }
  },
  methods: {
    extractRoot(payload) {
      return payload?.data?.data || payload?.data || payload || {}
    },
    fmtInt(v) {
      if (v === null || v === undefined || v === "") return "—"
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return String(n)
    },
    fmtDec(v) {
      if (v === null || v === undefined || v === "") return "—"
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return n.toFixed(2)
    },
    fmtDate(v) {
      if (!v) return "—"
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      return d.toLocaleDateString()
    },
    fmtDateTime(v) {
      if (!v) return "—"
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      return d.toLocaleString()
    },

    groupKeyForRow(r, mode) {
      if (mode === "orgid") return String(r?.orgId || r?.orgid || "").trim()
      if (mode === "underwriter") return String(r?.underwriterAzureId || r?.underwriterDisplayName || "").trim()
      if (mode === "loanOfficer") return String(r?.loanOfficer || r?.f317 || "").trim()
      if (mode === "division") return String(r?.division || "").trim()
      return String(r?.orgId || r?.orgid || "").trim()
    },

    groupTitleForRow(r, mode) {
      if (mode === "orgid") {
        const v = String(r?.orgId || r?.orgid || "").trim()
        return v ? `${v}` : "Org —"
      }
      if (mode === "underwriter") {
        const v = String(r?.underwriterDisplayName || "").trim()
        return v ? v : "Unknown UW"
      }
      if (mode === "loanOfficer") {
        const v = String(r?.loanOfficer || r?.f317 || "").trim()
        return v ? v : "Unknown LO"
      }
      if (mode === "division") {
        const v = String(r?.division || "").trim()
        return v ? v : "Division —"
      }
      return "Group"
    },

    groupSubForRow(r, mode) {
      if (mode === "orgid") {
        const uw = String(r?.underwriterDisplayName || "").trim()
        const lo = String(r?.loanOfficer || r?.f317 || "").trim()
        const parts = []
        if (uw) parts.push(`UW ${uw}`)
        if (lo) parts.push(`LO ${lo}`)
        return parts.length ? parts.join(" • ") : "—"
      }
      if (mode === "underwriter") return "Underwriter"
      if (mode === "loanOfficer") return "Loan Officer"
      if (mode === "division") return "Division"
      return "—"
    },

    sortGroupedList(list) {
      const mode = this.sortBy

      if (mode === "openDesc") {
        return list.sort((a, b) => Number(b.openConditionCount || 0) - Number(a.openConditionCount || 0))
      }

      if (mode === "division") {
        return list
          .slice()
          .sort((a, b) => String(a.groupTitle || "").localeCompare(String(b.groupTitle || "")))
      }

      return list
        .slice()
        .sort((a, b) => String(a.groupTitle || "").localeCompare(String(b.groupTitle || "")))
    },

    groupLoansByLoan(rows) {
      const map = {}

      for (const r of rows || []) {
        const key = r.loanId || r.loanNumber
        if (!key) continue

        if (!map[key]) {
          map[key] = {
            loanId: r.loanId,
            loanNumber: r.loanNumber,
            underwriterAzureId: r.underwriterAzureId,
            underwriterDisplayName: r.underwriterDisplayName,
            underwriterResolvedEmail: r.underwriterResolvedEmail,
            loanOfficer: r.loanOfficer || r.f317,
            orgId: r.orgId || r.orgid,
            submittalCounter: r.submittalCounter,
            logMSDateSubmittalDt: r.logMSDateSubmittalDt,
            logMSDateCondlApprovalDt: r.logMSDateCondlApprovalDt,
            openConditionCount: Number(r.openConditionCount || 0)
          }
        } else {
          map[key].openConditionCount += Number(r.openConditionCount || 0)
        }
      }

      return Object.values(map).sort((a, b) => Number(b.openConditionCount || 0) - Number(a.openConditionCount || 0))
    },

    loanTitle(r) {
      const n = r?.loanNumber ? String(r.loanNumber) : ""
      return n ? `Loan ${n}` : "Loan"
    },
    loanSub(r) {
      const uw = r?.underwriterDisplayName || "Unknown UW"
      const sub = this.fmtDate(r?.logMSDateSubmittalDt)
      const appr = this.fmtDate(r?.logMSDateCondlApprovalDt)
      return `${uw} • Sub ${sub} • Approved ${appr}`
    },
    detailKey(d) {
      return String(d.conditionId || "") + "-" + String(d.latestStatusOccurredAt || "") + "-" + String(d.title || "")
    },

    async fetchSummary() {
      this.loading = true
      this.error = ""
      try {
        const resp = await ApiService.GetLoansWithOpenConditionsAfterCondApprovalRolling90Days(this.authToken)
        const root = this.extractRoot(resp)
        const list = Array.isArray(root) ? root : Array.isArray(root?.rows) ? root.rows : Array.isArray(root?.data) ? root.data : []
        this.rows = Array.isArray(list) ? list : []
      } catch (e) {
        this.error = e?.message || "Failed to load"
      } finally {
        this.loading = false
      }
    },

    openGroupModal(g) {
      this.selectedGroup = g
      this.selectedLoan = null
      this.modalMode = "group"
      this.showModal = true
      this.detailsLoading = false
      this.detailsError = ""
      this.detailRows = []
    },
    async openLoanModal(r) {
      this.selectedLoan = r
      this.modalMode = "loan"
      this.detailsError = ""
      this.detailRows = []
      await this.fetchDetailsByLoanId(r.loanId)
    },
    backToGroup() {
      this.modalMode = "group"
      this.selectedLoan = null
      this.detailsLoading = false
      this.detailsError = ""
      this.detailRows = []
    },
    closeModal() {
      this.showModal = false
      this.modalMode = "group"
      this.selectedGroup = null
      this.selectedLoan = null
      this.detailsLoading = false
      this.detailsError = ""
      this.detailRows = []
    },

    async fetchDetailsByLoanId(loanId) {
      if (!loanId) {
        this.detailsError = "Missing loanId"
        return
      }

      this.detailsLoading = true
      this.detailsError = ""

      try {
        const resp = await ApiService.GetOpenConditionsAfterCondApprovalDrillIn(loanId, this.authToken)
        const root = this.extractRoot(resp)
        const list = Array.isArray(root) ? root : Array.isArray(root?.rows) ? root.rows : Array.isArray(root?.data) ? root.data : []
        this.detailRows = Array.isArray(list) ? list : []
      } catch (e) {
        this.detailsError = e?.message || "Failed to load details"
      } finally {
        this.detailsLoading = false
      }
    }
  }
}
</script>

<style scoped>
.ci-title-right{
  flex:0 0 auto;
  display:flex;
  align-items:center;
  gap:8px;
}

.ci-mini-search{
  height:28px;
  width: 140px;
  border-radius:10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.65);
  padding: 0 10px;
  font-weight: 900;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.85);
  outline: none;
}

.ci-mini-search:focus{
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.15);
}

.ci-mini-select{
  height:28px;
  border-radius:10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.65);
  padding: 0 10px;
  font-weight: 950;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.85);
  outline: none;
}


.ci-accent--productivity{ --accent-color:#2F80ED; }
.ci-accent--success{ --accent-color:#27AE60; }
.ci-accent--attention{ --accent-color:#F2994A; }
.ci-accent--insight{ --accent-color:#9B51E0; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
}

.ci-summary-group {
  min-height: 0;
  height: 100%;
  border: 1px solid var(--border-ltr);
  border-radius: 5px;
  background: var(--background);
  overflow: hidden;
  display: flex;
  flex-direction: column
}

.ci-summary-title {
  padding: 10px 12px;
  border-bottom: 1px solid var(--accent-color);
  background: rgba(242, 153, 74, 0.10);
  font-weight: 950;
  font-size: 12px;
  color: #F2994A;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height:50px;
}

.ci-title-left{
  display:flex;
  align-items:baseline;
  gap:10px;
  min-width:0;
}

.ci-title-right{
  flex:0 0 auto;
  display:flex;
  align-items:center;
  gap:8px;
}

.ci-mini-search{
  height:28px;
  width: 140px;
  border-radius:10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.65);
  padding: 0 10px;
  font-weight: 900;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.85);
  outline: none;
}

.ci-mini-search:focus{
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.15);
}

.ci-mini-select{
  height:28px;
  border-radius:10px;
  border:1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.65);
  padding: 0 10px;
  font-weight: 950;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.85);
  outline: none;
}

.ci-mini-select:focus{
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.15);
}

.ci-summary-subtitle {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-summary-cards {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-scroll {
  overflow: auto
}

.ci-summary-loading {
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 950;
  color: var(--text-secondary-on-background)
}

.ci-empty-inline {
  padding: 18px 10px;
  text-align: center;
  font-weight: 950;
  color: var(--text-secondary-on-background)
}

.ci-empty-inline--error {
  color: rgba(180, 0, 0, 0.80)
}

.ci-list {
  display: flex;
  flex-direction: column;
  gap: 10px
}

.ci-summary-row {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 5px;
  padding: 10px 12px;
  background: rgba(15, 23, 42, 0.015);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 44px;
  text-align: left;
  font: inherit;
  position: relative;
}

.ci-summary-row--click {
  cursor: pointer;
  outline: none
}

.ci-summary-row--click:hover {
  background: rgba(15, 23, 42, 0.03)
}

.ci-summary-namewrap {
  min-width: 0;
  flex: 1 1 auto;
  padding-right: 0;
}

.ci-summary-name {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis
}

.ci-summary-sub {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis
}

.ci-summary-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px
}

.ci-summary-metric {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap
}

.ci-pill-label {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-secondary-on-background)
}

.ci-pill-value {
  font-size: 14px;
  font-weight: 950;
  color: var(--text-primary-on-background);
  line-height: 1
}

.ci-avg-badge{
  flex:0 0 auto;
  font-size:10px;
  font-weight:950;
  padding:4px 8px;
  border-radius: 999px;
  border:1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.02);
  color: var(--text-secondary-on-background);
  line-height:1;
  white-space:nowrap;
}

.ci-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 9999
}

.ci-modal__panel {
  width: min(980px, 100%);
  max-height: min(85vh, 900px);
  background: var(--background);
  border-radius: 14px;
  border: 1px solid var(--border-ltr);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column
}

.ci-modal__header {
  padding: 12px;
  border-bottom: 1px solid var(--border-ltr);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: rgba(5, 29, 58, 0.03)
}

.ci-modal__head-left {
  min-width: 0
}

.ci-modal__head-right{
  display:flex;
  align-items:center;
  gap:10px;
}

.ci-modal__title {
  font-weight: 950;
  font-size: 13px;
  color: var(--text-primary-on-background);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis
}

.ci-modal__meta {
  margin-top: 8px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap
}

.ci-modal__close {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer
}

.ci-modal__close:hover {
  background: rgba(15, 23, 42, 0.06)
}

.ci-modal__body {
  padding: 12px;
  overflow: auto;
  min-height: 0
}

.ci-modal__footer {
  padding: 12px;
  border-top: 1px solid var(--border-ltr);
  display: flex;
  justify-content: flex-end
}

.ci-btn {
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  font-weight: 950;
  font-size: 12px
}

.ci-btn:hover {
  background: rgba(15, 23, 42, 0.06)
}

.ci-btn--ghost{
  background: transparent;
}
.ci-summary-right{
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
  flex-direction: column;
}

.ci-summary-top{
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 4px;
}
.ci-summary-right{
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}



.ci-summary-metric{
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
  font-size: 10px;
}

.ci-metric-label{
  font-weight: 700;
  color: var(--text-secondary-on-background);
  opacity: 0.75;
}

.ci-metric-value{
  font-weight: 950;
  color: var(--text-primary-on-background);
  line-height: 1;
}

.ci-summary-metric--muted .ci-metric-label{
  opacity: 0.6;
}

.ci-summary-metric--muted .ci-metric-value{
  opacity: 0.85;
}
.ci-metric-larger-font{
  font-size: 1.4rem;
}
</style>
